import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { TicketDetail as TicketDetailType } from '../types';
import { smarttix } from '../services/api';
import { theme } from '../theme';
import { STATUS_LABELS, SECONDARY_AXIS_MAX_VISIBLE } from '../constants';
import { BentoCard, FilterPanel, SelectionToggleIcon, AutoIcon, RestoreIcon, SpreadsheetRowIcon, DownloadArrowIcon, CheckCircleIcon, CrossIcon, TicketRowMeta, formatRowDate, ExpandIcon, PopoutIcon, HeaderIconButton } from './shared';
import { AutoButton } from './AutoButton';
import { SelectionStrip } from './SelectionStrip';
import { RueckgaengigToast } from './RueckgaengigToast';
import { CountUpNumber } from './CountUpNumber';
import { useSelectionMode } from '../hooks/useSelectionMode';
import { useCategoryMeta } from '../context/ClassificationSchemaContext';
import { useArchivedPagination, ArchiveBucket } from '../hooks/useArchivedPagination';
import { DateRangeValue } from './DateRangeInput';
import { listDetailFlex } from '../services/listDetailRatio';
import TicketDetailComponent from './TicketDetail';
import TicketDetailStage from './TicketDetailStage';
import { openTicketPopout } from './PopoutShell';
import { onTicketMutated, announceTicketMutated } from '../services/ticketSync';
import { PageType } from './NavBar';

// Bucket semantics (server-side, mirrors the StatistikSeite archive-mode KPI split):
//   Erledigt        = approved or sent      (rep approved, then archived)
//   Liegengeblieben = suggestion_ready       (AI processed, rep abandoned)
//   Ungeklärt       = pipeline_error OR KU OR unprocessed (never classified / unresolvable)
// FE label "Ungeklärt" (A0 §5.1); wire bucket param is 'ungeklaert' (unchanged,
// mapped inside useArchivedPagination). Row→bucket assignment is the BE's job.

/*
<purpose>Read-only archive browser with restore. Prevents ticket loss after the undo toast expires.</purpose>
<dependencies>useArchivedPagination → smarttix.getArchivedPage (server-side bucket/filter/cursor + truthful counts); smarttix.getTicket; smarttix.unarchiveTicket + bulk endpoints.</dependencies>
<constraints>No polling. First page on mount; reload after restore/auto actions. Infinite scroll via IntersectionObserver sentinel. No approve/reject/reanalysis actions.</constraints>
*/

interface ArchivSeiteProps {
  onNavigate: (page: PageType, params?: Record<string, string>) => void;
  // Tenant Eingangsweg visibility — same value the Antworten host and the pop-out receive, so the
  // one TicketDetail component cannot render differently depending on which page mounted it.
  showSourceChannel?: boolean;
  // Tenant-wide form-to-Excel capability, threaded from App's single /config read — same flag and
  // same gate as the Antworten page's toggle button.
  formExtractionEnabled?: boolean;
  // Tenant-wide inline-image counting rule, threaded from App's single /config read — a per-host
  // config read is forbidden, so this arrives as a prop like showSourceChannel above.
  attachmentsCountInline?: boolean;
}

const COMPACT_BREAKPOINT = 900;
// Includes BentoCard's own hover transition (box-shadow/transform) — BentoCard spreads `...style` last,
// so a bare `transition: flex` here would clobber the card-lift transition.
const MORPH_TRANSITION = 'flex 0.45s cubic-bezier(0.4,0,0.2,1), box-shadow 180ms ease-out, transform 180ms ease-out';

const ArchivSeite: React.FC<ArchivSeiteProps> = ({ onNavigate, showSourceChannel, formExtractionEnabled, attachmentsCountInline }) => {
  // The list header names the tenant's own axis, read from the same seam the rows use —
  // otherwise the header says one word and the badges beneath it say another.
  const cm = useCategoryMeta();
  // ─── State ───────────────────────────────────────────
  const [selectedUuid, setSelectedUuid] = useState<string | null>(null);
  const [selectedTicket, setSelectedTicket] = useState<TicketDetailType | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  // ─── Bucket filter chip + selection mode (Plan #2) ──
  const [bucket, setBucket] = useState<ArchiveBucket>(() => {
    const url = new URL(window.location.href);
    const v = url.searchParams.get('archive');
    if (v === 'liegengeblieben') return 'liegengeblieben';
    // 'fehlgeschlagen' kept as alias for backward compat (old bookmarks/links)
    if (v === 'unklassifiziert' || v === 'fehlgeschlagen') return 'unklassifiziert';
    return 'erledigt';
  });
  const updateBucket = (next: ArchiveBucket) => {
    const url = new URL(window.location.href);
    url.searchParams.set('archive', next);
    window.history.replaceState({}, '', url.toString());
    setBucket(next);
  };
  const sel = useSelectionMode();
  const [removingUuids, setRemovingUuids] = useState<Set<string>>(new Set());
  // Per-row in-flight restore (single-restore button). Local now that ArchivSeite
  // no longer routes through tm.archivedTickets / tm.restoringUuids.
  const [restoringUuids, setRestoringUuids] = useState<Set<string>>(new Set());
  const [undoToast, setUndoToast] = useState<{ count: number; verb: string; uuids: string[]; undo: 'archive' | 'unmark-erledigt' } | null>(null);
  const [infoToast, setInfoToast] = useState<string | null>(null);
  const [autoSubmitting, setAutoSubmitting] = useState(false);
  const [showErledigtModal, setShowErledigtModal] = useState(false);
  // Per-row Erledigen: uuid of the row that triggered the confirm modal (null = bulk).
  const [erledigtRowUuid, setErledigtRowUuid] = useState<string | null>(null);
  // Bucket switch clears selection — different rows visible.
  useEffect(() => { if (sel.mode) sel.exitMode(); }, [bucket]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Detail Stage (maximize/popout) ─────────────────
  const [detailMaximized, setDetailMaximized] = useState(false);
  // Selection change always drops the overlay.
  useEffect(() => { setDetailMaximized(false); }, [selectedUuid]);
  const restoreStage = useCallback(() => setDetailMaximized(false), []);
  const popoutFromStage = useCallback(() => {
    if (selectedUuid) openTicketPopout(selectedUuid);
    setDetailMaximized(false); // handoff: the window owns the ticket now
  }, [selectedUuid]);

  // Filters — all server-side now (no client-side useMemo). Date range is ISO from/to.
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [reasonCodeFilter, setReasonCodeFilter] = useState<string[]>([]);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterUser, setFilterUser] = useState('');
  const [dateRange, setDateRange] = useState<DateRangeValue>({ from: null, to: null });

  // Debounce the free-text search so each keystroke doesn't refetch a page.
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search), 250);
    return () => clearTimeout(id);
  }, [search]);

  // ─── Server-side data (rows / counts / cursor) ───────
  const archived = useArchivedPagination(bucket, useMemo(() => ({
    search: debouncedSearch,
    primaryAxis: categoryFilter,
    secondaryAxis: reasonCodeFilter,
    from: dateRange.from,
    to: dateRange.to,
    uploadedBy: filterUser,
  }), [debouncedSearch, categoryFilter, reasonCodeFilter, dateRange.from, dateRange.to, filterUser]));
  const rows = archived.rows;
  const erledigtCount = archived.counts.erledigt;
  const liegengebliebenCount = archived.counts.liegengeblieben;
  const ungeklaertCount = archived.counts.ungeklaert;
  // Header + badge for the active bucket come from counts, not loaded-row length.
  const activeBucketCount = bucket === 'erledigt' ? erledigtCount
    : bucket === 'liegengeblieben' ? liegengebliebenCount
    : ungeklaertCount;

  // ─── Infinite-scroll sentinel ────────────────────────
  // Callback ref so the observer (re)attaches the moment the sentinel node mounts
  // or unmounts — a static useRef + mount-only effect would miss it (the sentinel
  // only renders once the first page has rows). loadMore self-guards on cursor.
  const loadMoreRef = useRef(archived.loadMore);
  loadMoreRef.current = archived.loadMore;
  const observerRef = useRef<IntersectionObserver | null>(null);
  const sentinelRef = useCallback((node: HTMLDivElement | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    if (!node || typeof IntersectionObserver === 'undefined') return;
    const obs = new IntersectionObserver(entries => {
      if (entries.some(e => e.isIntersecting)) loadMoreRef.current();
    });
    obs.observe(node);
    observerRef.current = obs;
  }, []);

  // Foreign-window mutations: silent ledger refresh + detail refetch when it's ours.
  // `archived` is recreated every render (new hook object) — a stable ref lets the
  // listener (subscribed once, [] deps) always call the CURRENT refresh(). Silent
  // (never flips loading, never collapses loaded pages) — reload() stays reserved
  // for direct user actions (restore/auto) that legitimately want a fresh page 1.
  const selectedUuidRef = useRef(selectedUuid);
  selectedUuidRef.current = selectedUuid;
  const archivedRef = useRef(archived);
  archivedRef.current = archived;
  useEffect(() => onTicketMutated(async (uuid) => {
    archivedRef.current.refresh();
    const sel = selectedUuidRef.current;
    if (sel && (uuid === sel || uuid === '*')) {
      const detail = await smarttix.getTicket(sel);
      // Guard: selection may have moved on to a different ticket while this
      // getTicket() was in flight — don't clobber the NOW-selected ticket's
      // detail with a stale response for the ticket the user left behind.
      if (selectedUuidRef.current === sel && detail) setSelectedTicket(detail);
    }
  }), []);

  // Responsive
  const [isCompact, setIsCompact] = useState(window.innerWidth < COMPACT_BREAKPOINT);
  useEffect(() => {
    const onResize = () => setIsCompact(window.innerWidth < COMPACT_BREAKPOINT);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const loadDetail = useCallback(async (uuid: string) => {
    setDetailLoading(true);
    setError(null);
    try {
      const detail = await smarttix.getTicket(uuid);
      setSelectedTicket(detail);
    } catch {
      setSelectedTicket(null);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedUuid) loadDetail(selectedUuid);
    else setSelectedTicket(null);
  }, [selectedUuid, loadDetail]);

  // ─── Restore handler ────────────────────────────────
  const handleRestore = async (uuid: string) => {
    if (restoringUuids.has(uuid)) return;
    setActionLoading(true);
    setRestoringUuids(prev => new Set(prev).add(uuid));
    const ticket = rows.find(t => t.uuid === uuid);
    const res = await smarttix.unarchiveTicket(uuid);
    if (res.success) {
      const statusLabel = ticket?.previousStatus ? (STATUS_LABELS[ticket.previousStatus] || ticket.previousStatus) : 'Genehmigt';
      setToast(`Ticket wiederhergestellt — Status: ${statusLabel}`);
      setTimeout(() => setToast(null), 3000);
      // Drift note: this per-uuid mutation calls smarttix directly rather than a tm.*
      // mutator (ArchivSeite doesn't route through TicketManagerContext — see file
      // header), so it misses the tm-chokepoint announce. Announce explicitly here;
      // a popout window on THIS uuid needs to know it left the archive.
      announceTicketMutated(uuid);
      setSelectedUuid(null);
      setSelectedTicket(null);
      // Refresh archive page (row leaves).
      archived.reload();
    } else {
      setError(res.error || 'Fehler beim Wiederherstellen');
    }
    setRestoringUuids(prev => { const next = new Set(prev); next.delete(uuid); return next; });
    setActionLoading(false);
  };

  // Uploader options for the filter — derived from currently-loaded rows. Server-side
  // counts are bucket-wide, but this dropdown only needs plausible suggestions; the
  // server applies the actual uploadedBy filter.
  const distinctUploaders = useMemo(() => {
    const emails = new Set(rows.map(t => t.uploadedByEmail).filter(Boolean) as string[]);
    return Array.from(emails).sort();
  }, [rows]);

  const hasActiveFilters = !!categoryFilter || reasonCodeFilter.length > 0 || !!search || !!filterUser || !!dateRange.from || !!dateRange.to;
  // Browse → list-dominant 2:1; reading a selected ticket → detail-dominant 1:2 (row layout only).
  const ratio = listDetailFlex(selectedUuid);
  // For returning focus to the open row when the detail is closed via the X (keyboard continuity).
  const listRef = useRef<HTMLDivElement>(null);
  const closeDetail = () => {
    const row = selectedUuid ? listRef.current?.querySelector<HTMLElement>(`[data-uuid="${selectedUuid}"]`) : null;
    setSelectedUuid(null);
    (row ?? listRef.current)?.focus();
  };

  // ─── Auto action (Plan #2) — bucket-specific ────────
  // Erledigt: Auto = mass unarchive (bulkUnarchive). Rep restores to revisit.
  // Liegengeblieben: Auto = mark as Erledigt (bulkMarkErledigt) — keeps the ticket
  //   archived but moves it to the Erledigt bucket. Saves restore→approve→re-archive.
  // Ungeklärt: Auto = mass unarchive (rep restores to retry the pipeline).
  // Selection only operates on currently-loaded rows (server-side paginated set).
  const autoTargetUuids = sel.selected.size > 0
    ? rows.filter(t => sel.selected.has(t.uuid)).map(t => t.uuid)
    : [];
  const autoHasAction = autoTargetUuids.length > 0;
  // ⚠ SECONDARY SURFACE. Here an empty selection means NOTHING — on BOTH levers. That is the opposite
  // of the active list, where an empty selection under the excel lever means EVERY form-bearing ticket.
  // The asymmetry is deliberate: a mass download off the archive is not a gesture anyone asked for, and
  // the archive is where a careless "everything" would be largest. Do not unify the two pages.
  const autoIcon = sel.purpose === 'excel'
    ? <DownloadArrowIcon size={16} color={theme.colors.white} />
    : !autoHasAction
      ? <AutoIcon size={16} color={theme.colors.white} />
      : bucket === 'liegengeblieben'
        ? <CheckCircleIcon size={16} color={theme.colors.white} />
        : <RestoreIcon size={16} color={theme.colors.white} />;
  const autoNoActionMessage = bucket === 'liegengeblieben'
    ? 'Keine Auswahl zum Genehmigen.'
    : 'Keine Auswahl zum Wiederherstellen.';
  const autoActionTitle = bucket === 'liegengeblieben'
    ? `Genehmigen und im Archiv lassen (${autoTargetUuids.length})`
    : `Wiederherstellen (${autoTargetUuids.length})`;

  // One button, one handler; the active lever picks the verb. On this page the excel lever still
  // requires a selection — see the note at autoIcon.
  const handleAutoOrDownload = () => {
    if (sel.purpose === 'excel') {
      if (autoTargetUuids.length === 0) { setInfoToast('Keine Auswahl fuer den Excel-Download.'); return; }
      handleDownloadXlsx();
      return;
    }
    handleAutoClick();
  };

  const handleAutoClick = () => {
    if (autoSubmitting) return;
    if (!autoHasAction) { setInfoToast(autoNoActionMessage); return; }
    // Liegengeblieben → Erledigt is one-way (no Rückgängig). Confirm before firing.
    if (bucket === 'liegengeblieben') { setErledigtRowUuid(null); setShowErledigtModal(true); }
    else handleAutoUnarchive();
  };

  // Per-row Erledigen: opens the same confirm modal but scoped to a single uuid.
  const handlePerRowErledigen = (uuid: string) => {
    if (autoSubmitting) return;
    setErledigtRowUuid(uuid);
    setShowErledigtModal(true);
  };

  const handleAutoUnarchive = async () => {
    setAutoSubmitting(true);
    const uuids = autoTargetUuids.slice();
    setRemovingUuids(new Set(uuids));
    await new Promise(r => setTimeout(r, 200 + Math.min(40 * uuids.length, 2000)));
    try {
      const res = await smarttix.bulkUnarchive(uuids);
      if (res.error) {
        setError(res.error);
        setRemovingUuids(new Set());
      } else {
        setUndoToast({ count: res.unarchived, verb: 'wiederhergestellt', uuids, undo: 'archive' });
        announceTicketMutated('*');
        sel.exitMode();
        archived.reload();
        setRemovingUuids(new Set());
      }
    } finally {
      setAutoSubmitting(false);
    }
  };

  const handleAutoMarkErledigt = async () => {
    setShowErledigtModal(false);
    setAutoSubmitting(true);
    // Per-row: use the single uuid; bulk: use the selection.
    const uuids = erledigtRowUuid ? [erledigtRowUuid] : autoTargetUuids.slice();
    setErledigtRowUuid(null);
    setRemovingUuids(new Set(uuids));
    await new Promise(r => setTimeout(r, 200 + Math.min(40 * uuids.length, 2000)));
    try {
      const res = await smarttix.bulkMarkErledigt(uuids);
      if (res.error) {
        setError(res.error);
        setRemovingUuids(new Set());
      } else {
        setUndoToast({ count: res.markedErledigt, verb: 'genehmigt', uuids, undo: 'unmark-erledigt' });
        announceTicketMutated('*');
        sel.exitMode();
        // Why: refresh BEFORE clearing removingUuids (see AntwortenSeite for rationale).
        archived.reload();
        setRemovingUuids(new Set());
      }
    } finally {
      setAutoSubmitting(false);
    }
  };

  const handleAutoUndo = async () => {
    if (!undoToast) return;
    const { uuids, undo } = undoToast;
    setUndoToast(null);
    // 'archive' undoes a bulkUnarchive (re-archive the rows).
    // 'unmark-erledigt' undoes a bulkMarkErledigt — there's no dedicated endpoint;
    // unarchive+re-archive would alter previous_status incorrectly. So we skip the
    // BE round-trip and just clear the toast. Note: the Liegengeblieben→Erledigt
    // transition stays. Acceptable for v1 — operator can manually correct via SQL if needed.
    if (undo === 'archive') {
      await smarttix.bulkArchive(uuids);
      announceTicketMutated('*');
      archived.reload();
    }
  };

  // Why a download rather than a rendered table: the clerk's destination is Excel, and the workbook
  // carries per-cell notes (recognition confidence, where the two OCR passes disagreed, which values we
  // could not verify) that no HTML table would survive being pasted from. The row values are assembled
  // server-side, identically to the single-ticket copy, so the two delivery paths can never disagree
  // about what a cell says.
  const [xlsxBusy, setXlsxBusy] = useState(false);
  const handleDownloadXlsx = async () => {
    if (xlsxBusy) return;
    const uuids = autoTargetUuids;
    if (uuids.length === 0) { setInfoToast('Keine Auswahl fuer den Excel-Download.'); return; }

    setXlsxBusy(true);
    try {
      // Archive page has ONE gesture — an explicit pick. It never means "everything in this view"
      // (see the selection comment above), so it always sends uuids and never a selection.
      const res = await smarttix.downloadFormRowsXlsx({ uuids });
      if (res.error || !res.blob) { setInfoToast(res.error || 'Excel-Download fehlgeschlagen.'); return; }

      // Tickets without a form extraction produce no row. Saying so is the whole point: a clerk who
      // selected 15 and received 12 must be told, not left to count rows in the workbook.
      const skipped = res.matched - res.delivered;
      if (skipped > 0) {
        setInfoToast(`${res.delivered} von ${res.matched} Vorgaengen konnten uebernommen werden. `
          + `${skipped} enthalten kein lesbares Formular.`);
      }

      const url = URL.createObjectURL(res.blob);
      const a = document.createElement('a');
      a.href = url;
      // The name comes from the client, which composed it from the rows the server actually
      // included. Hardcoding it here once made the rename inert: the api returned the right
      // name and the anchor ignored it.
      a.download = res.fileName ?? 'Notizblatt.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Released on the next tick rather than immediately: revoking synchronously can cancel the
      // download in some browsers before it has read the object.
      setTimeout(() => URL.revokeObjectURL(url), 0);
    } finally {
      setXlsxBusy(false);
    }
  };

  // ─── Render ─────────────────────────────────────────
  return (
    <div style={{ fontFamily: theme.font.family, animation: 'stx-fadeIn 0.3s ease' }}>
      {/* Page title row — count + selection + Auto controls (Plan #2). */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: theme.spacing.md,
        paddingBottom: theme.spacing.md,
        borderBottom: `1px solid ${theme.colors.borderLight}`,
        marginBottom: theme.spacing.lg,
        flexWrap: 'wrap',
      }}>
        <h1 style={{
          fontSize: 22, fontWeight: 600, color: theme.colors.navy,
          margin: 0, flex: 1, minWidth: 200,
        }}>
          {archived.loading || activeBucketCount === 0 ? 'Archiv' : `${activeBucketCount} archivierte Ticket${activeBucketCount !== 1 ? 's' : ''}`}
        </h1>
        <SelectionStrip count={sel.selected.size} onClear={sel.clear} />
        {rows.length > 0 && (
          <button
            onClick={() => sel.setPurpose('archive')}
            aria-label={sel.purpose === 'archive' ? 'Auswahlmodus beenden' : 'Auswahlmodus starten'}
            title={sel.purpose === 'archive' ? 'Auswahlmodus beenden' : 'Auswahlmodus starten'}
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 40, height: 40, padding: 0,
              background: sel.purpose === 'archive' ? theme.colors.navy : 'transparent',
              border: `1px solid ${sel.purpose === 'archive' ? theme.colors.navy : theme.colors.borderLight}`,
              borderRadius: theme.borderRadius.sm,
              cursor: 'pointer',
              color: sel.purpose === 'archive' ? theme.colors.white : theme.colors.navyMid,
            }}
          >
            <SelectionToggleIcon size={14} color={sel.purpose === 'archive' ? theme.colors.white : theme.colors.navyMid} active={sel.purpose === 'archive'} />
          </button>
        )}

        {/* Form-to-excel lever, sibling of the multi-select beside it. Gated on the tenant
            capability first — a role that does not own it must never see the affordance exists. */}
        {formExtractionEnabled && rows.length > 0 && (
          <button
            onClick={() => sel.setPurpose('excel')}
            aria-label={sel.purpose === 'excel' ? 'Formular-Excel-Auswahl beenden' : 'Formularzeilen als Excel auswaehlen'}
            title={sel.purpose === 'excel' ? 'Formular-Excel-Auswahl beenden' : 'Formularzeilen als Excel auswaehlen'}
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 40, height: 40, padding: 0,
              background: sel.purpose === 'excel' ? theme.colors.navy : 'transparent',
              border: `1px solid ${sel.purpose === 'excel' ? theme.colors.navy : theme.colors.borderLight}`,
              borderRadius: theme.borderRadius.sm,
              cursor: 'pointer',
              color: sel.purpose === 'excel' ? theme.colors.white : theme.colors.navyMid,
            }}
          >
            <SpreadsheetRowIcon size={14} color={sel.purpose === 'excel' ? theme.colors.white : theme.colors.navyMid} />
          </button>
        )}
        <AutoButton
          icon={autoIcon}
          onClick={handleAutoOrDownload}
          title={sel.purpose === 'excel'
            ? (autoTargetUuids.length > 0
                ? `Formularzeilen der Auswahl als Excel herunterladen (${autoTargetUuids.length})`
                : 'Keine Auswahl fuer den Excel-Download.')
            : (autoHasAction ? autoActionTitle : autoNoActionMessage)}
        />
        {/* Liegengeblieben bulk: offer Wiederherstellen as a second verb alongside Erledigen */}
        {bucket === 'liegengeblieben' && (
          <AutoButton
            icon={<RestoreIcon size={16} color={theme.colors.white} />}
            onClick={() => {
              if (autoSubmitting) return;
              if (!autoHasAction) { setInfoToast('Keine Auswahl zum Wiederherstellen.'); return; }
              handleAutoUnarchive();
            }}
            title={autoHasAction ? `Wiederherstellen (${autoTargetUuids.length})` : 'Keine Auswahl zum Wiederherstellen.'}
          />
        )}
      </div>

      {/* 2-column layout */}
      <div style={{
        display: 'flex', gap: theme.spacing.lg,
        flexDirection: isCompact ? 'column' : 'row',
        ...(isCompact ? {} : { height: 'calc(100vh - 200px)', minHeight: 400 }),
      }}>
        {/* Left: archived ticket list */}
        <BentoCard
          title="Archivierte Tickets"
          style={{
            display: 'flex', flexDirection: 'column', minWidth: 0,
            flex: isCompact ? 1 : ratio.list,
            ...(isCompact ? { maxHeight: 500 } : { transition: MORPH_TRANSITION }),
          }}
          contentStyle={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', flex: 1, padding: 0 }}
        >
          <FilterPanel
            search={search}
            onSearchChange={setSearch}
            open={filterOpen}
            onOpenChange={setFilterOpen}
            categoryFilter={categoryFilter}
            onCategoryFilterChange={setCategoryFilter}
            reasonCodeFilter={reasonCodeFilter}
            onReasonCodeFilterChange={setReasonCodeFilter}
            uploaders={distinctUploaders}
            filterUser={filterUser}
            onFilterUserChange={setFilterUser}
            from={dateRange.from}
            to={dateRange.to}
            onDateRangeChange={setDateRange}
            dateRangeLabel="Archiviert (Zeitraum)"
            onClearAll={() => {
              setSearch(''); setCategoryFilter(null);
              setReasonCodeFilter([]); setFilterUser(''); setDateRange({ from: null, to: null });
            }}
            hasActiveFilters={hasActiveFilters}
          />

          {/* Bucket tabs (below the search/filter bar, matches DashboardSeite.Verlauf pattern) */}
          <div style={{
            display: 'flex',
            borderBottom: `1px solid ${theme.colors.borderLight}`,
            marginBottom: theme.spacing.sm,
            userSelect: 'none',
          }}>
            {(['erledigt', 'liegengeblieben', 'unklassifiziert'] as const).map(b => {
              const active = bucket === b;
              const label = b === 'erledigt' ? 'Erledigt' : b === 'liegengeblieben' ? 'Liegengeblieben' : 'Ungeklärt';
              const count = b === 'erledigt' ? erledigtCount : b === 'liegengeblieben' ? liegengebliebenCount : ungeklaertCount;
              return (
                <button key={b} onClick={() => updateBucket(b)} title={label} style={{
                  flex: 1, minWidth: 0,
                  display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 4,
                  padding: '8px 0', background: 'none',
                  border: 'none', borderBottom: `2px solid ${active ? theme.colors.navy : 'transparent'}`,
                  fontSize: 13, fontWeight: active ? 600 : 400,
                  color: active ? theme.colors.navy : theme.colors.textSecondary,
                  cursor: 'pointer', fontFamily: theme.font.family,
                }}>
                  {/* Label ellipsizes, count stays — keeps the tab one line so the list:detail morph doesn't reflow it. */}
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{label}</span>
                  <span style={{ flexShrink: 0 }}>({count})</span>
                </button>
              );
            })}
          </div>

          {/* Column headers */}
          <div style={{
            display: 'flex', alignItems: 'center',
            padding: `${theme.spacing.xs}px ${theme.spacing.md}px ${theme.spacing.xs}px ${4 + theme.spacing.sm}px`,
            fontSize: 11, fontWeight: 600, color: theme.colors.textSecondary,
            textTransform: 'uppercase', letterSpacing: 0.5,
            borderBottom: `1px solid ${theme.colors.borderLight}`,
            userSelect: 'none',
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>Betreff / {cm.categoryNoun}</div>
            <div style={{ width: bucket === 'liegengeblieben' ? 80 : 28, flexShrink: 0, textAlign: bucket === 'liegengeblieben' ? 'right' : 'center' }}>
              {bucket === 'liegengeblieben' ? 'Aktionen' : ''}
            </div>
          </div>

          {/* Rows */}
          <div ref={listRef} tabIndex={-1} style={{ flex: 1, overflowY: 'auto', outline: 'none' }}>
            {archived.loading ? (
              <div style={{ padding: 40, textAlign: 'center', color: theme.colors.textSecondary, fontSize: 13 }}>
                Lade Archiv...
              </div>
            ) : archived.error ? (
              <div style={{ padding: 40, textAlign: 'center', color: theme.colors.error, fontSize: 13 }}>
                {archived.error}
                <div style={{ marginTop: theme.spacing.sm }}>
                  <button
                    onClick={() => archived.reload()}
                    style={{
                      padding: '6px 14px', fontSize: 13, fontWeight: 500,
                      fontFamily: theme.font.family,
                      background: 'transparent', color: theme.colors.navy,
                      border: `1px solid ${theme.colors.borderLight}`,
                      borderRadius: theme.borderRadius.sm, cursor: 'pointer',
                    }}
                  >Erneut versuchen</button>
                </div>
              </div>
            ) : rows.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: theme.colors.textSecondary, fontSize: 13 }}>
                {hasActiveFilters ? 'Keine Treffer' : 'Keine archivierten Tickets'}
              </div>
            ) : (
              rows.map((t, idx) => {
                const isSelected = selectedUuid === t.uuid;
                const isPicked = sel.selected.has(t.uuid);
                const isRemoving = removingUuids.has(t.uuid);
                return (
                  <div
                    key={t.uuid}
                    data-uuid={t.uuid}
                    className={isRemoving ? 'stx-archiving' : undefined}
                    // Phase 2 follow-up — was role=button + aria-pressed, but the row
                    // contains the child Restore button which axe (correctly) flags as
                    // nested-interactive. The row stays keyboard-operable via tabIndex
                    // + onKeyDown; aria-label gives SR users the row's name.
                    tabIndex={0}
                    aria-label={`Archiviertes Ticket ${t.subject ?? t.uuid}`}
                    onClick={() => {
                      if (sel.mode) { sel.toggle(t.uuid); return; }
                      setSelectedUuid(isSelected ? null : t.uuid);
                    }}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        if (sel.mode) { sel.toggle(t.uuid); return; }
                        setSelectedUuid(isSelected ? null : t.uuid);
                      }
                    }}
                    style={{
                      display: 'flex', alignItems: 'center',
                      backgroundColor: (isPicked || isSelected) ? theme.colors.bgLight : (idx % 2 === 1 ? theme.colors.bgPage : 'transparent'),
                      borderLeft: isPicked ? `4px solid ${theme.colors.navy}` : '4px solid transparent',
                      borderBottom: `1px solid ${theme.colors.borderLight}`,
                      cursor: 'pointer',
                      opacity: restoringUuids.has(t.uuid) ? 0 : 1,
                      transition: 'opacity 0.3s ease, background-color 0.15s ease',
                      animation: 'stx-fadeIn 0.3s ease',
                      ...(isRemoving ? { transitionDelay: `${idx * 40}ms` } : {}),
                    }}
                  >
                    {/* Content — archive ledger: no category strip (the selection stripe at the
                        row's left edge is the only stripe); category shows as a badge in line 2. */}
                    <div style={{
                      flex: 1, display: 'flex', alignItems: 'center',
                      padding: `${theme.spacing.xs}px ${theme.spacing.md}px ${theme.spacing.xs + 6}px ${theme.spacing.sm}px`,
                      minWidth: 0,
                    }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        {/* Line 1: subject (muted) + archived date (right) */}
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                          <div style={{
                            flex: 1, minWidth: 0, fontSize: 13, fontWeight: 500, color: theme.colors.navyMid,
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                          }}>
                            {t.subject || '(Kein Betreff)'}
                          </div>
                          <span style={{ flexShrink: 0, fontSize: 11, color: theme.colors.navyMuted }}>
                            {formatRowDate(t.archivedAt || t.updatedAt)}
                          </span>
                        </div>
                        {/* Line 2: shared compact meta — badge · reason · 📎 · status (right) */}
                        <TicketRowMeta ticket={t} maxSecondaryAxisVisible={SECONDARY_AXIS_MAX_VISIBLE.responses} compact attachmentsCountInline={attachmentsCountInline} />
                      </div>

                      {/* Per-row action buttons — Liegengeblieben: Erledigen + Wiederherstellen; others: Wiederherstellen only */}
                      <div style={{
                        width: bucket === 'liegengeblieben' ? 80 : 28,
                        flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
                        gap: 2,
                      }}>
                        {bucket === 'liegengeblieben' && (
                          <button
                            onClick={e => { e.stopPropagation(); handlePerRowErledigen(t.uuid); }}
                            disabled={autoSubmitting || restoringUuids.has(t.uuid)}
                            aria-label="Erledigen"
                            title="Erledigen (Genehmigen und im Archiv lassen)"
                            style={{
                              background: 'none', border: 'none', cursor: 'pointer',
                              color: theme.colors.navyMuted, fontSize: 14, padding: 2,
                              opacity: (autoSubmitting || restoringUuids.has(t.uuid)) ? 0.3 : 0.6,
                              transition: 'opacity 0.15s ease',
                            }}
                            onMouseEnter={e => { e.currentTarget.style.opacity = '1'; }}
                            onMouseLeave={e => { e.currentTarget.style.opacity = '0.6'; }}
                          >
                            <CheckCircleIcon size={14} color="currentColor" />
                          </button>
                        )}
                        <button
                          onClick={e => { e.stopPropagation(); handleRestore(t.uuid); }}
                          disabled={restoringUuids.has(t.uuid)}
                          aria-label="Wiederherstellen"
                          title="Wiederherstellen"
                          style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            color: theme.colors.navyMuted, fontSize: 14, padding: 2,
                            opacity: restoringUuids.has(t.uuid) ? 0.3 : 0.6,
                            transition: 'opacity 0.15s ease',
                          }}
                          onMouseEnter={e => { e.currentTarget.style.opacity = '1'; }}
                          onMouseLeave={e => { e.currentTarget.style.opacity = '0.6'; }}
                        >
                          {/* Restore/undo icon: counterclockwise arrow */}
                          <svg aria-hidden="true" focusable="false" width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M2 3v4h4" />
                            <path d="M2 7a6 6 0 1 1 1.76 4.24" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}

            {/* Infinite-scroll sentinel — observed only once mounted; loadMore
                self-guards on nextCursor + in-flight. Kept inside the scroll
                container so IntersectionObserver uses it as the implicit root. */}
            {!archived.loading && !archived.error && rows.length > 0 && (
              <div ref={sentinelRef} aria-hidden="true" style={{ height: 1 }} />
            )}
            {archived.loadingMore && (
              <div style={{ padding: 16, textAlign: 'center', color: theme.colors.textSecondary, fontSize: 12 }}>
                Lade weitere…
              </div>
            )}
          </div>
        </BentoCard>

        {/* Right: detail panel */}
        <BentoCard
          title="Details"
          headerAction={selectedUuid ? (
            <div style={{ display: 'flex', gap: 4, animation: 'stx-fadeIn 0.2s ease' }}>
              <HeaderIconButton label="Maximieren" onClick={() => setDetailMaximized(true)}><ExpandIcon size={15} /></HeaderIconButton>
              <HeaderIconButton label="In eigenem Fenster öffnen" onClick={popoutFromStage}><PopoutIcon size={15} /></HeaderIconButton>
              <HeaderIconButton label="Auswahl aufheben" onClick={closeDetail}><CrossIcon size={16} /></HeaderIconButton>
            </div>
          ) : undefined}
          style={{
            display: 'flex', flexDirection: 'column', minWidth: 0,
            ...(isCompact
              ? { width: '100%', flexShrink: 0, minHeight: 300 }
              : { flex: ratio.detail, transition: MORPH_TRANSITION }),
          }}
          contentStyle={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto', padding: theme.spacing.md }}
        >
          {selectedUuid ? (
            <TicketDetailStage
              maximized={detailMaximized}
              onRestore={restoreStage}
              onPopout={popoutFromStage}
              title={selectedTicket?.subject || '(Kein Betreff)'}
            >
              <TicketDetailComponent
              onInfo={setInfoToast}
                ticket={selectedTicket!}
                loading={detailLoading}
                actionLoading={actionLoading}
                error={error}
                onClearError={() => setError(null)}
                onApprove={() => {}}
                onReject={() => {}}
                onDownload={() => {}}
                onRestore={() => handleRestore(selectedUuid)}
                showSourceChannel={showSourceChannel}
                attachmentsCountInline={attachmentsCountInline}
              />
            </TicketDetailStage>
          ) : (
            <div style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: theme.colors.textSecondary, fontSize: 13,
            }}>
              Ticket auswählen
            </div>
          )}
        </BentoCard>
      </div>

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
          padding: `${theme.spacing.sm}px ${theme.spacing.lg}px`,
          backgroundColor: theme.colors.navy, color: theme.colors.white,
          borderRadius: theme.borderRadius.md, fontSize: 13, fontWeight: 500,
          boxShadow: theme.shadows.cardHover, animation: 'stx-fadeIn 0.2s ease',
          zIndex: 1000,
        }}>
          {toast}
        </div>
      )}

      <RueckgaengigToast
        visible={!!undoToast}
        message={undoToast ? (
          <><CountUpNumber target={undoToast.count} /> Ticket{undoToast.count !== 1 ? 's' : ''} {undoToast.verb}</>
        ) : ''}
        onUndo={undoToast?.undo === 'archive' ? handleAutoUndo : undefined}
        onDismiss={() => setUndoToast(null)}
      />

      <RueckgaengigToast
        visible={!!infoToast}
        message={infoToast ?? ''}
        onDismiss={() => setInfoToast(null)}
      />

      {/* Genehmigen-und-archivieren confirm modal (one-way action — no Rückgängig). */}
      {showErledigtModal && (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,13,102,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 200,
          animation: 'stx-fadeIn 0.15s ease',
        }} onClick={() => !autoSubmitting && setShowErledigtModal(false)}>
          <div onClick={e => e.stopPropagation()} style={{
            backgroundColor: theme.colors.white, borderRadius: theme.borderRadius.md,
            padding: `${theme.spacing.lg}px ${theme.spacing.lg + theme.spacing.sm}px`,
            minWidth: 320, maxWidth: 440,
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
            fontFamily: theme.font.family,
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: theme.spacing.sm,
              marginBottom: theme.spacing.sm,
            }}>
              <h2 style={{
                fontSize: 16, fontWeight: 600, color: theme.colors.navy,
                margin: 0, flex: 1,
              }}>Genehmigen und im Archiv lassen?</h2>
              <button
                onClick={() => !autoSubmitting && setShowErledigtModal(false)}
                aria-label="Schließen"
                style={{
                  background: 'none', border: 'none', cursor: 'pointer', padding: 4,
                  display: 'inline-flex', alignItems: 'center',
                }}
              >
                <CrossIcon size={14} color={theme.colors.navyMuted} />
              </button>
            </div>
            <p style={{
              fontSize: 13, color: theme.colors.navyMid,
              margin: `0 0 ${theme.spacing.lg}px 0`, lineHeight: 1.5,
            }}>
              {(() => { const n = erledigtRowUuid ? 1 : autoTargetUuids.length; return `${n} Ticket${n !== 1 ? 's' : ''}`; })()} · diese Aktion ist nicht rückgängig zu machen.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: theme.spacing.sm }}>
              <button
                onClick={() => setShowErledigtModal(false)}
                disabled={autoSubmitting}
                style={{
                  padding: '6px 14px', fontSize: 13, fontWeight: 500,
                  fontFamily: theme.font.family,
                  background: 'transparent', color: theme.colors.navyMid,
                  border: `1px solid ${theme.colors.borderLight}`,
                  borderRadius: theme.borderRadius.sm,
                  cursor: autoSubmitting ? 'not-allowed' : 'pointer',
                }}
              >Abbrechen</button>
              <button
                onClick={handleAutoMarkErledigt}
                disabled={autoSubmitting}
                style={{
                  padding: '6px 14px', fontSize: 13, fontWeight: 600,
                  fontFamily: theme.font.family,
                  background: theme.colors.navy, color: theme.colors.white,
                  border: 'none', borderRadius: theme.borderRadius.sm,
                  cursor: autoSubmitting ? 'not-allowed' : 'pointer',
                  opacity: autoSubmitting ? 0.6 : 1,
                }}
              >{autoSubmitting ? 'Wird genehmigt…' : 'Genehmigen'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArchivSeite;
